import { Box, Chip, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ChevronRight, PhotoCamera, Science } from '@mui/icons-material';
import type { TestListingRow } from '../../services/testListingService';
import { TEST_STATUS_COLORS } from '../../constants/testStatusColors';

interface MyTestsListProps {
  rows: TestListingRow[];
  /** WOD key → count of required evidence fields still missing (0 / absent = none). */
  evidenceMissing?: Map<string, number>;
  onOpen: (row: TestListingRow) => void;
  /** Group rows under WorkOrderId · ProjectName headers (default true). */
  grouped?: boolean;
}

/**
 * Tap-friendly test cards for phones/iPads — the mobile counterpart of the
 * desktop Tests table. Used by the mobile Dashboard preview and the My Tests
 * page. One tap opens the test entry page; an amber camera pill flags tests
 * with required field evidence still missing.
 */
export const MyTestsList = ({ rows, evidenceMissing, onOpen, grouped = true }: MyTestsListProps) => {
  const groups = new Map<string, TestListingRow[]>();
  if (grouped) {
    rows.forEach((r) => {
      const key = `${r.WorkOrderId} · ${r.ProjectName}`;
      groups.set(key, [...(groups.get(key) ?? []), r]);
    });
  } else {
    groups.set('', rows);
  }

  const card = (row: TestListingRow) => {
    const missing = evidenceMissing?.get(row.WorkOrderDetailKey) ?? 0;
    const statusColor = TEST_STATUS_COLORS[row.TestStatus] || '#999';
    return (
      <Paper
        key={row.WorkOrderDetailKey}
        elevation={0}
        onClick={() => onOpen(row)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.25, p: 1.25,
          borderRadius: '10px', border: '1px solid', borderColor: alpha('#4A6741', 0.18),
          cursor: 'pointer', transition: 'background-color 0.15s ease',
          '&:hover': { backgroundColor: alpha('#4A6741', 0.05) },
          '&:active': { backgroundColor: alpha('#4A6741', 0.1) },
        }}
      >
        <Box sx={{
          width: 38, height: 38, borderRadius: '9px', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: alpha('#4A6741', 0.09),
        }}>
          <Science sx={{ fontSize: 19, color: '#4A6741' }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 650, lineHeight: 1.25 }} noWrap>
            {row.TestName} · {row.SpecimenRef || row.SampleReference}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }} noWrap>
            {row.LocationName}{row.SpecimenDepth != null ? ` · ${row.SpecimenDepth} m` : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {missing > 0 && (
            <Chip
              size="small"
              icon={<PhotoCamera sx={{ fontSize: '12px !important' }} />}
              label={missing}
              sx={{
                height: 20, fontSize: '0.65rem', fontWeight: 700,
                backgroundColor: alpha('#ED6C02', 0.12), color: '#B26A00',
                '& .MuiChip-icon': { color: '#B26A00' },
              }}
            />
          )}
          <Chip size="small" label={row.TestStatus}
            sx={{
              height: 20, fontSize: '0.62rem', fontWeight: 700,
              backgroundColor: alpha(statusColor, 0.12), color: statusColor,
            }} />
          <ChevronRight sx={{ fontSize: 18, color: 'text.disabled' }} />
        </Box>
      </Paper>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {[...groups.entries()].map(([label, groupRows]) => (
        <Box key={label || 'all'}>
          {label && (
            <Typography variant="overline" sx={{
              fontWeight: 700, color: 'primary.main', fontSize: '0.66rem',
              letterSpacing: 0.6, display: 'block', mb: 0.5, px: 0.25,
            }} noWrap>
              {label}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {groupRows.map(card)}
          </Box>
        </Box>
      ))}
    </Box>
  );
};
